import {
  Alert,
  Check,
  DateFormat,
  EquipUIFireflyV2Theme,
  getDaysFromNowUntil,
} from '@equip.health/ui';
import { createElement } from 'react';
import { DateTime } from 'luxon';

import {
  type SVGElementType,
  ActivityField,
  NULL_USER_NAME,
  OrganizeBy,
  activityFieldMapper,
} from '../constants/task.constants';
import {
  UNASSIGNED_PATIENT,
  UNASSIGNED_USER,
  UNASSIGNED_PROVIDER,
} from '../constants/user.constants';
import { getDisplayNameOrLegalNameForProvider } from './user.util';
import { GroupClassListTypeEnum } from '~/lib/constants/groupClasses.constants';

const { neutral, tertiary, critical, secondary, success, primary, background } =
  EquipUIFireflyV2Theme.colors;

export enum TaskPriorityValue {
  LOW,
  MEDIUM,
  HIGH,
}

type TaskPriorityDefinition = {
  title: string;
  value: string;
  color: string;
};

export const taskPriorityDefinitions: TaskPriorityDefinition[] = [
  {
    color: primary[100],
    title: 'Low',
    value: '1',
  },
  {
    color: tertiary.marigold.base,
    title: 'Medium',
    value: '2',
  },
  {
    color: critical.base,
    title: 'High',
    value: '3',
  },
];

export enum TaskStatusValue {
  TO_DO,
  IN_PROGRESS,
  DONE,
  VOID,
  CLOSED,
}

export enum TaskStatusLabel {
  TO_DO = 'To do',
  IN_PROGRESS = 'In progress',
  DONE = 'Done',
  VOID = 'Void',
  CLOSED = 'Closed',
}

type TaskStatusDefinition = {
  color: string;
  textColor: string;
  title: TaskStatusLabel;
  value: string;
};

export const taskStatusDefinitions: TaskStatusDefinition[] = [
  {
    color: 'secondary.10',
    textColor: 'secondary.70',
    title: TaskStatusLabel.TO_DO,
    value: '1',
  },
  {
    color: 'primary.10',
    textColor: background.button['primary.active'],
    title: TaskStatusLabel.IN_PROGRESS,
    value: '2',
  },
  {
    color: 'tertiary.marigold.background',
    textColor: 'tertiary.marigold.text',
    title: TaskStatusLabel.DONE,
    value: '3',
  },
  {
    color: 'neutral.line',
    textColor: 'neutral.secondary',
    title: TaskStatusLabel.VOID,
    value: '4',
  },
  {
    color: 'neutral.line',
    textColor: 'neutral.secondary',
    title: TaskStatusLabel.CLOSED,
    value: '5',
  },
];

export const TaskCategoryColorMapper: Record<string, string> = {
  admissions: secondary[50],
  inquiry: critical.base,
  medical_records: tertiary.marigold.base,
  outreach: primary[100],
  patient_care: secondary[10],
  provider_assignments: neutral.primary,
  referral: tertiary.marigold.base,
  revenue_cycle: success.base,
  review: neutral.line,
};

export enum TaskOrderByDate {
  TODAY = 'Today',
  THIS_WEEK = 'This week',
  NEXT_WEEK = 'Next week',
  LAST_WEEK = 'Last week',
  LATER = 'Later',
  EARLIER = 'Earlier',
}

export const getEmptyTaskContent = (
  key: string,
  isFilterActive: boolean,
): string => {
  return {
    description: isFilterActive
      ? "If you didn't find what you're looking for, try to reduce your filters or add new tasks."
      : 'Is this the best moment to create that task before you forget?',
    primaryText: isFilterActive
      ? 'Oops. Nothing to see here.'
      : 'Everything done here!',
    secondaryText: isFilterActive
      ? "We couldn't find anything to show you."
      : "You don't have any tasks now.",
  }[key];
};

export const isOpenTask = (task: Task, includeDone = false): boolean => {
  const openStatuses = [
    taskStatusDefinitions[TaskStatusValue.TO_DO].value,
    taskStatusDefinitions[TaskStatusValue.IN_PROGRESS].value,
  ];
  if (includeDone) {
    openStatuses.push(taskStatusDefinitions[TaskStatusValue.DONE].value);
  }
  return openStatuses.includes((task?.taskStatusId ?? '').toString());
};

export const getOrganizeByDateLabel = (
  date: string,
  isDueDate: boolean,
): string => {
  const numDays = DateTime.fromFormat(date, 'LL/dd/yyyy')
    .diff(DateTime.now().startOf('day'), 'days')
    .as('days');

  if (!isDueDate) {
    return numDays < 0 ? TaskOrderByDate.EARLIER : TaskOrderByDate.TODAY;
  }

  if (isDueDate && numDays <= 0) return TaskOrderByDate.TODAY;
  if (isDueDate && numDays > 0 && numDays <= 8)
    return TaskOrderByDate.THIS_WEEK;
  if (isDueDate && numDays > 8 && numDays < 15)
    return TaskOrderByDate.NEXT_WEEK;

  return TaskOrderByDate.LATER;
};

export const organizeByDate = (
  tasks: Task[],
  isDueDate: boolean,
): Record<string, Task[]> => {
  const dateDataMap: Record<string, Task[]> = {};
  tasks.forEach((task: Task) => {
    const dateLabel = getOrganizeByDateLabel(
      isDueDate ? task.dueDate : task.createdDate,
      isDueDate,
    );
    dateDataMap[dateLabel] = [...(dateDataMap[dateLabel] ?? []), task];
  });

  return dateDataMap;
};

export const organizeByCategory = (tasks: Task[]): Record<string, Task[]> => {
  const categoryDataMap: Record<string, Task[]> = {};
  tasks.forEach((task: Task) => {
    const categoryLabel = task.category?.categoryName ?? 'Unassigned';
    categoryDataMap[categoryLabel] = [
      ...(categoryDataMap[categoryLabel] ?? []),
      task,
    ];
  });
  return Object.fromEntries(
    Object.entries(categoryDataMap).sort(([keyA], [keyB]) =>
      keyA.localeCompare(keyB),
    ),
  );
};

export const organizeByPatient = (tasks: Task[]): Record<string, Task[]> => {
  const unassignedTasks: Task[] = [];
  const sortedTasksByPatient: Record<string, Task[]> = {};
  tasks.forEach((task) => {
    const {
      userId = '',
      firstName = '',
      chosenName = '',
      lastName = '',
    } = task.patient ?? {};

    if (userId) {
      const patientLabel = `${userId}::${firstName}::${chosenName}::${lastName}`;
      sortedTasksByPatient[patientLabel] = [
        ...(sortedTasksByPatient[patientLabel] ?? []),
        task,
      ];
    } else {
      unassignedTasks.push(task);
    }
  });

  return {
    // Rebuild sortedTasksByPatient map with patients sorted by last name alphabetically
    ...Object.fromEntries(
      Object.entries(sortedTasksByPatient).sort((a, b) => {
        return a[0].split('::')[3].localeCompare(b[0].split('::')[3]);
      }),
    ),
    Unassigned: unassignedTasks,
  };
};

export const organizeByPriorityScore = (
  tasks: Task[],
): Record<string, Task[]> => {
  return {
    Tasks: tasks,
  };
};

export const isValidName = (title: string): boolean => {
  const { length } = title ?? '';
  return length > 0;
};

export const parseTaskParamFilterValues = (
  filters: TTaskFilters,
): Record<keyof TTaskFilters, string[]> =>
  Object.fromEntries(
    Object.entries(filters ?? {}).map(
      ([filterKey, { value }]: [keyof TTaskFilters, FilterType]) => [
        filterKey,
        value.map(
          (autoCompleteOption: AutoCompleteOption) => autoCompleteOption.value,
        ),
      ],
    ),
  ) as Record<keyof TTaskFilters, string[]>;

export const getEditedDueDate = (
  newDueDate: string, // mm/dd/yyyy
  originalDueDate: string, // yyyy-mm-dd
): string => {
  // return the original if it's the same as new due date.
  if (originalDueDate && originalDueDate.length > 0) {
    const originalFormatted = DateTime.fromFormat(
      originalDueDate,
      'LL/dd/yyyy',
    );
    if (newDueDate === originalFormatted.toString()) {
      return originalFormatted.toFormat('yyyy-LL-dd');
    }
  }
  // return "null" if new dueDate is not valid.
  if (newDueDate && newDueDate.length > 0) {
    const dueDateTime = DateTime.fromFormat(newDueDate, 'LL/dd/yyyy');
    if (!dueDateTime.isValid) {
      return null;
    }
    return dueDateTime.toFormat('yyyy-LL-dd');
  }

  // otherwise, no update was made to the orignial "null" date.
  return null;
};

export const formatTaskDate = (
  dateString: string,
  isFrom: boolean,
  isDueDate: boolean,
): string => {
  // setting milliseconds is necessary to catch dates that are set to the exact end of day
  const formattedDate = DateTime.fromFormat(dateString, 'LL/dd/yyyy HH:mm:ss', {
    zone: 'utc',
  }).set({
    millisecond: isFrom ? 0 : 999,
  });

  return isDueDate
    ? formattedDate.toFormat('yyyy-LL-dd')
    : formattedDate.toFormat("yyyy-LL-dd'T'HH:mm:ss.SSS");
};

export const getTaskParamsFromFilters = (
  activeFilters: Record<keyof TTaskFilters, string[]>,
): Record<string, string | string[]> => {
  const params: Record<string, string | string[]> = {};

  (
    [
      { keyName: 'assignee', paramName: 'assignee-external-id' },
      { keyName: 'patient', paramName: 'patient-external-id' },
      { keyName: 'category', paramName: 'task-category-external-id' },
      { keyName: 'creator', paramName: 'creator-external-id' },
      { keyName: 'priority', paramName: 'priority' },
      { keyName: 'status', paramName: 'status' },
      { keyName: 'dueDateFrom', paramName: 'start-date' },
      { keyName: 'dueDateTo', paramName: 'end-date' },
      { keyName: 'createdDateFrom', paramName: 'task-created-start-date' },
      { keyName: 'createdDateTo', paramName: 'task-created-end-date' },
      { keyName: 'title', paramName: 'task-name' },
    ] as { keyName: keyof TTaskFilters; paramName: string }[]
  ).forEach(({ keyName, paramName }) => {
    const paramValue = activeFilters[keyName];

    if (paramValue.length) {
      if (['dueDateFrom', 'dueDateTo'].includes(keyName)) {
        params[paramName] = formatTaskDate(
          paramValue[0],
          keyName === 'dueDateFrom',
          true,
        );
      } else if (['createdDateFrom', 'createdDateTo'].includes(keyName)) {
        params[paramName] = formatTaskDate(
          paramValue[0],
          keyName === 'createdDateFrom',
          false,
        );
      } else {
        params[paramName] = paramValue;
      }
    }
  });

  return params;
};

export const isValidOrganizeBy = (organizeBy: OrganizeBy): boolean =>
  [
    OrganizeBy.CATEGORY,
    OrganizeBy.DUE_DATE,
    OrganizeBy.CREATED_DATE,
    OrganizeBy.PATIENT,
    OrganizeBy.PRIORITY_SCORE,
  ].includes(organizeBy);

export const invalidNameError = 'Title must be between 1 - 100 characters';

export const filterInActiveSubCategories = (subs: Category[]): Category[] =>
  subs.filter(({ isActive }: Category): boolean => isActive);

/**
 * inbound outreach" subcategory is set only for task which are created for Inquiry or Referral.
 * filterDisabledSubCategories - is to filter given list of disabledSubCategories.
 */
const disabledSubCategories = ['inbound outreach'];
export const filterDisabledSubCategories = (subs: Category[]): Category[] =>
  subs.filter(
    ({ categoryName }: Category): boolean =>
      !disabledSubCategories.includes(categoryName.toLowerCase()),
  );
export const isDisabledSubCategory = (sub: string): boolean =>
  disabledSubCategories.some(
    (subcat: string) => subcat.toLowerCase() === sub?.toLowerCase(),
  );

export const getPatientAndLabIdFromUrl = (
  urlString: string,
): { patientId: string; labId: string } | null => {
  const url = new URL(urlString);
  const pathSegments = url.pathname.split('/').filter(Boolean);

  const patientIdIndex = pathSegments.indexOf('patients') + 1;
  const labIdIndex = pathSegments.indexOf('labs') + 1;

  if (
    patientIdIndex > 0 &&
    labIdIndex > 0 &&
    pathSegments[patientIdIndex] &&
    pathSegments[labIdIndex]
  ) {
    const patientId = pathSegments[patientIdIndex];
    const labId = pathSegments[labIdIndex];
    return { labId, patientId };
  }

  return null;
};

export type CompServTask = {
  assignee?: {
    assignedAt?: string;
    avatarUrl?: string;
    avatarThumbnailUrl?: string;
    externalId: string;
    firstName: string;
    lastName: string;
    providerType: string;
    providerTypeAtAssignment?: string;
    displayFirstName?: string;
    displayLastName?: string;
    avatarDetails?: AvatarDetails;
    outOfOffice?: OutOfOfficeItem[];
  };
  createdBy: {
    externalId: string;
    firstName: string;
    lastName: string;
  };
  category: {
    categoryColor?: string;
    categoryExternalId: string;
    categoryName: string;
  };
  completedByFamilyUser?: {
    chosenName: string;
    externalId: string;
    firstName: string;
    lastName: string;
    userType: string;
  };
  isFamilyTask: boolean;
  familyAssignees?: {
    chosenName?: string;
    externalId: string;
    firstName: string;
    lastName: string;
    userType: 'support' | 'patient';
  }[];
  patient?: {
    chosenName?: string; // for when we start sending this from the API
    externalId: string;
    firstName: string;
    lastName: string;
    gender: string;
    dateOfBirth: string;
  };
  status: {
    taskStatusId: string;
    taskStatusName: string;
  };
  subCategory?: {
    categoryExternalId: string;
    categoryName: string;
  };
  taskDescription: string;
  dueDate: string;
  taskCreatedDate: string;
  taskExternalId: string;
  taskName: string;
  taskPriorityId: string;
  taskPriorityScore?: number;
  voidReason?: string;
  voidReasonExternalId?: string;
};

export type FetchTasksRequest = {
  filterBy: Record<string, string | number | boolean | string[]>;
};

export type FetchTasksResponse = {
  data: CompServTask[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
};

export const mapCompServTaskToTask = (
  task: CompServTask,
  categories: Category[],
): Task => {
  const {
    assignee: compServAssignee,
    category: compServCategory,
    createdBy: compServCreator,
    familyAssignees,
    isFamilyTask,
    patient: compServPatient,
    status,
    subCategory: compServSubCategory,
    taskCreatedDate,
    taskDescription: description,
    dueDate,
    taskExternalId,
    taskName: name,
    taskPriorityId,
    taskPriorityScore,
    voidReason,
    voidReasonExternalId,
  } = task;

  const category = (categories ?? []).find(
    ({ categoryExternalId }: Category) =>
      categoryExternalId === compServCategory?.categoryExternalId,
  );
  if (category?.categoryExternalId) {
    category.categoryColor =
      TaskCategoryColorMapper[
        category.categoryName.replace(/ /g, '_').toLowerCase()
      ];
  }

  const subcategory =
    (category?.subCategoryList ?? []).find(
      ({ categoryExternalId }: Category) =>
        categoryExternalId === compServSubCategory?.categoryExternalId,
    ) ?? null;

  const {
    chosenName: patientChosenName,
    externalId: patientId,
    firstName: patientFirst,
    lastName: patientLast,
    dateOfBirth,
    gender,
  } = compServPatient ?? {};

  const {
    avatarUrl,
    externalId: assigneeId,
    firstName: assigneeFirst,
    lastName: assigneeLast,
    providerType,
    providerTypeAtAssignment,
    displayFirstName: assigneeDisplayNameFirst,
    displayLastName: assigneeDisplayNameLast,
    avatarDetails,
    outOfOffice,
  } = compServAssignee ?? {};

  const {
    externalId: creatorId,
    firstName: creatorFirst,
    lastName: creatorLast,
  } = compServCreator ?? {};

  return {
    assignee: assigneeId
      ? ({
          avatarDetails,
          avatar: avatarUrl ? { url: avatarUrl } : undefined,
          displayFirstName: assigneeDisplayNameFirst,
          displayLastName: assigneeDisplayNameLast,
          firstName: assigneeFirst,
          lastName: assigneeLast,
          providerTypeAtAssignment,
          providerTypeName: providerType,
          userId: assigneeId,
          outOfOffice,
        } as GetProviderByIdResponse)
      : UNASSIGNED_PROVIDER,

    category,
    createdDate: DateTime.fromISO(taskCreatedDate).toFormat('LL/dd/yyyy'),
    /** @TODO creator should be included in the comp service task payload */
    creator: {
      firstName: creatorFirst,
      lastName: creatorLast,
      userId: creatorId,
    },
    description,
    dueDate: dueDate
      ? DateTime.fromFormat(dueDate, 'yyyy-LL-dd').toFormat('LL/dd/yyyy')
      : undefined,
    serverDueDate: dueDate
      ? DateTime.fromFormat(dueDate, 'yyyy-LL-dd').toFormat('LL/dd/yyyy')
      : undefined,
    familyAssignees,
    isFamilyTask,
    name,
    patient: patientId
      ? {
          chosenName: patientChosenName,
          dateOfBirth,
          firstName: patientFirst,
          gender,
          lastName: patientLast,
          userId: patientId,
        }
      : null,
    subcategory,
    taskExternalId,
    taskPriorityId,
    taskPriorityScore,

    taskStatusId: status.taskStatusId,

    voidReason,
    voidReasonExternalId,
  };
};

export const getTaskListItemCategoryDisplay = (
  category: Category,
  subcategory: Category,
): string =>
  `${category?.categoryName ?? ''}${
    subcategory?.categoryName ? ` (${subcategory.categoryName})` : ''
  }`;

export const getTaskListItemDaysFromNow = (
  dueDate: string,
): number | undefined =>
  getDaysFromNowUntil(
    DateTime.fromJSDate(new Date(dueDate)).toFormat(DateFormat.longDate),
  );

export const getTaskListItemBgColor = (
  daysFromNow: number,
  status: TaskStatusLabel,
): string => {
  if (
    [
      TaskStatusLabel.DONE,
      TaskStatusLabel.VOID,
      TaskStatusLabel.CLOSED,
    ].includes(status)
  )
    return 'neutral.background.secondary';

  let bgColor = 'red.10';
  if (daysFromNow > 0) bgColor = 'neutral.background.primary';
  if (daysFromNow === 0) bgColor = 'tertiary.marigold.background';
  return bgColor;
};

export const getTaskListItemBorderColor = (
  daysFromNow: number,
  status: TaskStatusLabel,
): string => {
  if (
    [
      TaskStatusLabel.DONE,
      TaskStatusLabel.VOID,
      TaskStatusLabel.CLOSED,
    ].includes(status)
  )
    return 'neutral.line';

  let borderColor = 'red.25';
  if (daysFromNow > 0) borderColor = 'neutral.line';
  if (daysFromNow === 0) borderColor = 'tertiary.marigold.base';
  return borderColor;
};

export const getTaskActivityIcon = (
  activityField: ActivityField,
  value: string | number,
): SVGElementType => {
  if (activityField === ActivityField.STATUS_ID) {
    const { value: voidValue } = taskStatusDefinitions[TaskStatusValue.VOID];
    const { value: closedValue } =
      taskStatusDefinitions[TaskStatusValue.CLOSED];
    const { value: doneValue } = taskStatusDefinitions[TaskStatusValue.DONE];

    const stringVal = String(value);
    if (stringVal === doneValue) return Check;
    if (stringVal === voidValue || stringVal === closedValue) return Alert;
  }
  return activityFieldMapper[activityField].icon;
};

export const getVoidReasonData = (
  voidReasons: VoidReason[],
  reasonExtId: string,
  key: keyof VoidReason,
): string =>
  (voidReasons ?? []).find(
    ({ externalVoidReasonId }) => externalVoidReasonId === reasonExtId,
  )?.[key];

const getTaskStatusFromId = (id: string): string =>
  taskStatusDefinitions[Number.parseInt(id, 10) - 1]?.title;

const getTaskPriorityFromId = (id: string): string =>
  taskPriorityDefinitions[Number.parseInt(id, 10) - 1]?.title;

export const replaceNullProvider = (value: string): string => {
  return value === NULL_USER_NAME ? UNASSIGNED_USER.firstName : value;
};

const replaceNullPatient = (value: string): string => {
  return value === NULL_USER_NAME ? UNASSIGNED_PATIENT.firstName : value;
};

const formatValueMapper: Record<
  ActivityField,
  (value?: string, disableQuotes?: boolean) => string
> = {
  [ActivityField.CREATED]: () =>
    activityFieldMapper[ActivityField.CREATED].name,
  [ActivityField.STATUS_ID]: (value: string) => getTaskStatusFromId(value),
  [ActivityField.PRIORITY_ID]: (value: string) => getTaskPriorityFromId(value),
  [ActivityField.TASK_DUE_DATE]: (value: string) =>
    DateTime.fromFormat(value, 'yyyy-LL-dd').toFormat('LL/dd/yyyy'),
  [ActivityField.TASK_NAME]: (value: string, disableQuotes?: boolean) =>
    disableQuotes ? null : `"${value}"`,
  [ActivityField.ASSIGNEE_NAME]: (value: string) => replaceNullProvider(value),
  [ActivityField.PATIENT_NAME]: (value: string) => replaceNullPatient(value),
  [ActivityField.CLOSED_OR_VOID_REASON]: (value: string) =>
    `${value} reason update`,
  [ActivityField.CATEGORY_NAME]: () => null,
  [ActivityField.SUBCATEGORY_NAME]: () => null,
};

export const formatValue = (
  value: string,
  field: ActivityField,
  disableQuotes = false,
): string => formatValueMapper[field]?.(value, disableQuotes) ?? value;

const formatSubtextMapper: Record<
  | ActivityField.CREATED
  | ActivityField.STATUS_ID
  | ActivityField.CLOSED_OR_VOID_REASON,
  (voidReason?: string, value?: string) => string
> = {
  [ActivityField.CREATED]: () =>
    activityFieldMapper[ActivityField.CREATED].name,
  [ActivityField.STATUS_ID]: (voidReason: string, value: string) =>
    voidReason ? `${value} because ${voidReason}` : null,
  [ActivityField.CLOSED_OR_VOID_REASON]: (voidReason: string) =>
    `Changed to ${voidReason}`,
};

export const formatSubtext = ({
  fieldChanged,
  newValue,
  previousValue,
  voidReason,
}: {
  fieldChanged: ActivityField;
  newValue: string;
  previousValue?: string;
  voidReason?: string;
}): string => {
  const formattedValue = formatValue(newValue, fieldChanged);
  const subtextFromMapper = formatSubtextMapper[fieldChanged]?.(
    voidReason,
    formattedValue,
  );
  if (subtextFromMapper) return subtextFromMapper;

  const fieldName = activityFieldMapper[fieldChanged].name;
  const fromText = previousValue
    ? ` from ${formatValue(previousValue, fieldChanged)}`
    : '';
  return `${fieldName} changed${fromText} to ${formattedValue}`;
};

const ACTIVITY_CHANGES_TO_LOG = Object.values(ActivityField);

const shouldIgnoreDueDateActivity = (
  field: ActivityField,
  activity: TaskActivityEvent,
  prevActivity: TaskActivityEvent,
): boolean =>
  field === ActivityField.TASK_DUE_DATE &&
  DateTime.fromJSDate(new Date(prevActivity[field])).toFormat(
    DateFormat.longDate,
  ) ===
    DateTime.fromJSDate(new Date(activity[field])).toFormat(
      DateFormat.longDate,
    );

const shouldIgnoreVoidReasonActivity = (
  field: ActivityField,
  activity: TaskActivityEvent,
  prevActivity: TaskActivityEvent,
): boolean =>
  field === ActivityField.CLOSED_OR_VOID_REASON &&
  activity[ActivityField.STATUS_ID] !== prevActivity[ActivityField.STATUS_ID];

export const getChangedActivityFields = (
  activity: TaskActivityEvent,
  prevActivity: TaskActivityEvent,
): string[] =>
  Object.keys(activity ?? []).filter(
    (field: ActivityField) =>
      ACTIVITY_CHANGES_TO_LOG.includes(field) &&
      activity[field] !== prevActivity[field] &&
      !shouldIgnoreDueDateActivity(field, activity, prevActivity) &&
      !shouldIgnoreVoidReasonActivity(field, activity, prevActivity),
  );

export const isPatientTaskDetailRoute = (path: string): boolean => {
  return /^\/maud\/patients\/\S+\/tasks\/\S+$/.test(path);
};

export const getCombinedSortedTasksForPatient = (
  maudTasksData?: FetchTasksResponse,
  familyTasksData?: FetchTasksResponse,
) => {
  return [
    ...(maudTasksData?.data ?? []),
    ...(familyTasksData?.data ?? []),
  ].sort((a, b) => {
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });
};

const linkRegex = /\[([^\]]+)\]\((http[s]?:\/\/[^\s)]+)\)/g;
export const parseDescriptionForUrlMarkup = (
  text: string,
  LinkComponent: (props: {
    color: string;
    href: string;
    isExternal: boolean;
    target: string;
  }) => JSX.Element,
  shouldShowInSameTab: boolean = false,
) => {
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;

  text.replace(linkRegex, (match, linkText, url, index) => {
    if (lastIndex !== index) {
      parts.push(text.substring(lastIndex, index));
    }

    parts.push(
      createElement(
        LinkComponent,
        {
          color: 'blue.500',
          href: url,
          isExternal: !shouldShowInSameTab,
          key: url + index,
          target: shouldShowInSameTab ? '_self' : '_blank',
        },
        linkText,
      ),
    );

    lastIndex = index + match.length;
    return match;
  });

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
};

export const getAssigneeNameOrUnassigned = (
  activity: TaskActivityEvent,
): string => {
  const ASSIGNEE_NAME_UNASSIGNED = 'Unassigned';
  const assigneeName = activity?.assigneeName ?? '';
  if (assigneeName.toLowerCase() === ASSIGNEE_NAME_UNASSIGNED.toLowerCase()) {
    return ASSIGNEE_NAME_UNASSIGNED;
  }
  return getDisplayNameOrLegalNameForProvider(
    activity?.assigneeDisplayFirstName,
    activity?.assigneeFirstName,
    activity?.assigneeDisplayLastName,
    activity?.assigneeLastName,
  );
};

export const getEmptyCaption = (
  groupClassTitle: string,
  listType: GroupClassListType,
): string => {
  if (groupClassTitle.length > 0) {
    return 'No results found';
  }

  if (listType === GroupClassListTypeEnum.ACTIVE) {
    return 'There are no active groups';
  }

  if (listType === GroupClassListTypeEnum.ARCHIVE) {
    return 'There are no archived groups';
  }

  return 'You are not currently hosting any groups';
};
